// EmailAssistPanel — AI + SOP-based response-point suggester for the Inbox
// thread reader. Suggest-only: it never writes or sends a finished email. The
// VRM reads a vendor thread, clicks "Suggest response points", and the backend
// streams back bullet-pointed POINTS grounded in a vendor-communication SOP
// segment. The VRM weaves them into their own reply (there's a Copy button).
//
// SSE: the suggest endpoint is a POST that returns text/event-stream, so we
// can't use EventSource (GET-only) and axios/crmApi can't stream. We use the
// exact fetch + getReader + TextDecoder + `data: `-line pattern the rest of
// this codebase uses for streaming AI (see useAiStream.js, WorkSummaryGenerator).
//
// Markdown rendering (bullets, **bold**, headers) is adapted from the
// email-insights AIInsightsPanel so styling stays consistent.

import React, { useMemo, useRef, useState } from "react";
import { Select as AntSelect } from "antd";
import { Sparkles, RefreshCw, Copy, Check, AlertCircle, Tag } from "lucide-react";
import toast from "react-hot-toast";
import { SERVER_URL } from "../../../utils/serverConfig";
import { useUpdateLead } from "../hooks/useLeads";
import { useEmailAssistSegments } from "../hooks/useEmails";

// Inline markdown: **bold**, *italic*, `code`. Adapted verbatim in spirit from
// email-insights/AIInsightsPanel.jsx renderInline(), retuned to CRM theme vars.
function renderInline(text) {
  const parts = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[2]) parts.push(<strong key={match.index} style={{ fontWeight: 600 }}>{match[2]}</strong>);
    else if (match[3]) parts.push(<em key={match.index}>{match[3]}</em>);
    else if (match[4]) parts.push(
      <code key={match.index} style={{ fontSize: 12, padding: "1px 5px", borderRadius: 4, background: "var(--color-bg-muted)" }}>{match[4]}</code>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : text;
}

// Block-level markdown renderer. Handles `# h1`, `## h2`, `- ` / `* ` bullets,
// `1.` ordered lists, and paragraphs. Mirrors AIInsightsPanel's block loop,
// simplified for the streaming bullet-point shape this endpoint returns.
function renderMarkdown(md) {
  const lines = String(md).split("\n");
  const out = [];
  let list = null; // { ordered: bool, items: [] }

  const flushList = (key) => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    out.push(
      <Tag key={`l${key}`} style={{ paddingLeft: 22, margin: "4px 0 10px" }}>
        {list.items.map((it, j) => (
          <li key={j} style={{ lineHeight: 1.65, color: "var(--color-text-primary)", marginBottom: 5, fontSize: 13 }}>
            {renderInline(it)}
          </li>
        ))}
      </Tag>
    );
    list = null;
  };

  lines.forEach((raw, i) => {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { flushList(i); return; }

    if (/^#\s/.test(line)) {
      flushList(i);
      out.push(
        <div key={i} style={{ fontSize: 15, fontWeight: 600, color: "var(--crm-primary)", margin: "8px 0 8px" }}>
          {renderInline(line.replace(/^#\s+/, ""))}
        </div>
      );
      return;
    }
    if (/^#{2,}\s/.test(line)) {
      flushList(i);
      out.push(
        <div key={i} style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)", margin: "8px 0 6px" }}>
          {renderInline(line.replace(/^#{2,}\s+/, ""))}
        </div>
      );
      return;
    }
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    if (bullet) {
      if (!list || list.ordered) { flushList(i); list = { ordered: false, items: [] }; }
      list.items.push(bullet[1]);
      return;
    }
    const ordered = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ordered) {
      if (!list || !list.ordered) { flushList(i); list = { ordered: true, items: [] }; }
      list.items.push(ordered[1]);
      return;
    }
    flushList(i);
    out.push(
      <p key={i} style={{ lineHeight: 1.65, color: "var(--color-text-primary)", margin: "0 0 8px", fontSize: 13 }}>
        {renderInline(line)}
      </p>
    );
  });
  flushList("end");
  return out;
}

export default function EmailAssistPanel({ accountId, threadId, leadId, compact = false, hideHeaderChrome = false }) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState(""); // status line ("Using SOP #3 — …")
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);       // { message, code }
  const [copied, setCopied] = useState(false);
  // VRM's explicit segment choice from the always-visible dropdown. When set it
  // overrides the lead's stored vendorSegment for this run.
  const [selectedSegment, setSelectedSegment] = useState("");
  // leadId the backend resolved from the thread (via EmailLink) — lets us save
  // a segment pick to the right lead even when this panel wasn't handed a leadId
  // prop (the global Inbox case). Falls back to the prop.
  const [resolvedLeadId, setResolvedLeadId] = useState(null);
  const abortRef = useRef(null);
  const updateLead = useUpdateLead();
  const effectiveLeadId = leadId || resolvedLeadId;

  // The 5 SOP segments — always available (react-query, cached). Powers the
  // always-visible dropdown.
  const { data: segments = [] } = useEmailAssistSegments();

  // Core streaming call. `segmentKey` (optional) overrides the lead's stored
  // vendorSegment; defaults to the dropdown selection if the caller didn't pass one.
  const run = async (segmentKeyArg) => {
    const segmentKey = segmentKeyArg || selectedSegment || undefined;
    if (!accountId || !threadId || streaming) return;
    // Cancel any in-flight stream first.
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setStreaming(true);
    setError(null);
    setText("");
    setStatus("");
    setCopied(false);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${SERVER_URL}/api/v1/crm/email-assist/${accountId}/threads/${threadId}/suggest`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ leadId: leadId || undefined, segmentKey: segmentKey || undefined }),
          signal: abortRef.current.signal,
        }
      );

      if (!res.ok || !res.body) {
        throw new Error(`Request failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n");
        buffer = parts.pop() || "";
        for (const line of parts) {
          if (!line.startsWith("data: ")) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === "status") {
              setStatus(d.message || "");
              if (d.leadId) setResolvedLeadId(d.leadId);
            } else if (d.type === "content") {
              setText((prev) => prev + (d.text || ""));
            } else if (d.type === "error") {
              setError({ message: d.message || "Something went wrong", code: d.code });
              if (d.leadId) setResolvedLeadId(d.leadId);
            } else if (d.type === "done") {
              // stream finished
            }
          } catch (_) {
            /* ignore malformed SSE line */
          }
        }
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        setError({ message: err?.message || "Failed to generate suggestions" });
      }
    } finally {
      setStreaming(false);
    }
  };

  // Dropdown change: remember the pick for the next run, and — if this thread is
  // linked to a lead — persist it on the lead so the tag sticks for next time.
  // Does NOT auto-run; the VRM hits Suggest when ready.
  const onPickSegment = async (segmentKey) => {
    setSelectedSegment(segmentKey || "");
    if (segmentKey && effectiveLeadId) {
      try {
        await updateLead.mutateAsync({ id: effectiveLeadId, patch: { vendorSegment: segmentKey } });
        toast.success("Vendor segment tagged");
      } catch (_) {
        toast.error("Couldn't save segment on the lead");
      }
    }
  };

  const copy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  const rendered = useMemo(() => renderMarkdown(text), [text]);
  const hasContent = text.trim().length > 0;
  const isNoSegment = error?.code === "no_segment";

  const outerStyle = hideHeaderChrome
    ? { width: "100%" }
    : { marginTop: compact ? 0 : 20, width: "100%" };
  const cardStyle = hideHeaderChrome
    ? { borderRadius: 0, background: "transparent", border: "none", padding: 0 }
    : {
        borderRadius: 8,
        background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.03) 100%)",
        border: "1px solid var(--color-border-subtle)",
        padding: 16,
      };

  return (
    <div style={outerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: hasContent || streaming || error ? 14 : 0 }}>
          {hideHeaderChrome ? (
            // Drawer already shows the icon+title; just surface the status line.
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
              {status || "SOP-grounded suggestions — you write the reply"}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8,
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(99,102,241,0.25)", flexShrink: 0,
              }}>
                <Sparkles size={15} color="#fff" />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--color-text-primary)", letterSpacing: "-0.005em" }}>
                  AI response points
                </div>
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {status || "SOP-grounded suggestions — you write the reply"}
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {hasContent && !streaming && (
              <button
                type="button"
                onClick={copy}
                title="Copy suggestions"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "6px 10px", fontSize: 12, fontWeight: 600,
                  background: "transparent", border: "1px solid var(--color-border-subtle)",
                  borderRadius: 8, cursor: "pointer",
                  color: copied ? "var(--crm-success)" : "var(--color-text-secondary)",
                  transition: "all 120ms",
                }}
              >
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
              </button>
            )}
            <button
              type="button"
              onClick={() => run()}
              disabled={streaming}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", fontSize: 12, fontWeight: 600,
                background: streaming ? "var(--crm-primary-soft)" : "var(--crm-primary)",
                color: streaming ? "var(--crm-primary)" : "#fff",
                border: "none", borderRadius: 8,
                cursor: streaming ? "wait" : "pointer",
                boxShadow: streaming ? "none" : "0 2px 6px rgba(99,102,241,0.25)",
                transition: "all 120ms", whiteSpace: "nowrap",
              }}
            >
              {streaming
                ? <><RefreshCw size={13} className="animate-spin" /> Generating…</>
                : <><Sparkles size={13} /> {hasContent ? "Regenerate" : "Suggest response points"}</>}
            </button>
          </div>
        </div>

        {/* Always-visible vendor-segment picker — VRM chooses which SOP to ground
            suggestions in before (or after) generating. Overrides the lead's
            stored segment for this run. */}
        <div style={{ marginTop: 12, marginBottom: hasContent || streaming || error ? 14 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 5 }}>
            <Tag size={12} /> Vendor segment
          </div>
          <AntSelect
            value={selectedSegment || undefined}
            onChange={(v) => onPickSegment(v)}
            disabled={streaming}
            allowClear
            placeholder={effectiveLeadId ? "Use the lead's tagged segment" : "Choose a segment…"}
            // This panel also renders inside the compose Modal (z-1100), where an
            // antd popup at its default 1050 would open behind it.
            styles={{ popup: { root: { zIndex: 1300 } } }}
            popupMatchSelectWidth={320}
            style={{ width: "100%" }}
            optionLabelProp="label"
            options={segments.map((s) => ({
              value: s.segmentKey,
              label: `${s.sopNumber != null ? `SOP #${s.sopNumber} — ` : ""}${s.title}`,
              title: s.oneLineSummary || undefined,
            }))}
          />
        </div>

        {/* Error — no_segment gets a friendly, actionable state */}
        {error && !streaming && (
          <div style={{
            background: isNoSegment ? "var(--crm-primary-soft)" : "var(--crm-danger-soft)",
            color: isNoSegment ? "var(--crm-primary)" : "var(--crm-danger)",
            borderRadius: 8, padding: 12,
            display: "flex", flexDirection: "column", gap: 10,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              {isNoSegment ? <Tag size={15} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />}
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                {isNoSegment
                  ? "Tag this vendor with a segment first so the AI knows which SOP to ground its suggestions in."
                  : error.message}
              </div>
            </div>

            {isNoSegment ? (
              <div style={{ fontSize: 12, color: "var(--crm-primary)", opacity: 0.85 }}>
                Choose a <strong>Vendor segment</strong> above, then hit Suggest again.
              </div>
            ) : (
              <button
                type="button"
                onClick={() => run()}
                style={{
                  alignSelf: "flex-start",
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", fontSize: 12, fontWeight: 600,
                  background: "var(--color-bg-surface)", border: "1px solid var(--crm-danger)",
                  borderRadius: 8, cursor: "pointer", color: "var(--crm-danger)",
                }}
              >
                <RefreshCw size={13} /> Retry
              </button>
            )}
          </div>
        )}

        {/* Streamed content */}
        {(hasContent || (streaming && !error)) && (
          <div style={{ paddingTop: 2 }}>
            {rendered}
            {streaming && (
              <span
                aria-hidden
                style={{
                  display: "inline-block", width: 7, height: 14,
                  background: "var(--crm-primary)", borderRadius: 2,
                  marginLeft: 2, verticalAlign: "text-bottom",
                  animation: "crmAssistBlink 1s steps(2) infinite",
                }}
              />
            )}
            <style>{`@keyframes crmAssistBlink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
          </div>
        )}

        {/* Idle streaming hint (button clicked, nothing yet) */}
        {streaming && !hasContent && !error && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text-secondary)", paddingTop: 2 }}>
            {status || "Reading the thread and the vendor SOP…"}
          </div>
        )}
      </div>
    </div>
  );
}
